import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { FileVaultApiService } from '../../services/file-vault-api.service';
import {
  CommonUser,
  CommonRole,
  CommonPermission,
  CommonUserDetail
} from '../../models/file-vault.models';

@Component({
  selector: 'fv-user-role-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonsModule, InputsModule, DropDownsModule],
  template: `
    <div class="user-role-admin">
      <div class="header">
        <div>
          <h2>User Roles & Permissions</h2>
          <p class="subtitle">Assign roles and direct permissions to users from the Common directory.</p>
        </div>
        <div class="actions">
          <label class="toggle">
            <input type="checkbox" [(ngModel)]="showInactive" (change)="loadUsers()" />
            Show inactive
          </label>
          <button kendoButton [look]="'flat'" (click)="refresh()">Refresh</button>
        </div>
      </div>

      <div class="error" *ngIf="errorMessage">{{ errorMessage }}</div>

      <div class="layout">
        <div class="sidebar">
          <input kendoTextBox
                 [(ngModel)]="searchTerm"
                 (ngModelChange)="applyFilter()"
                 placeholder="Search users..." />
          <div class="user-list" *ngIf="filteredUsers.length > 0">
            <button kendoButton
                    *ngFor="let user of filteredUsers"
                    [look]="selectedDetail?.user?.id === user.id ? 'default' : 'flat'"
                    (click)="selectUser(user)">
              <div class="user-name">{{ user.displayName }}</div>
              <div class="user-login">{{ user.loginId }}</div>
            </button>
          </div>
          <div class="empty" *ngIf="filteredUsers.length === 0">No users found.</div>
        </div>

        <div class="detail" *ngIf="selectedDetail">
          <div class="detail-header">
            <h3>{{ selectedDetail.user.displayName }}</h3>
            <span class="login">{{ selectedDetail.user.loginId }} · {{ selectedDetail.user.email }}</span>
          </div>

          <div class="section">
            <label>Role</label>
            <kendo-dropdownlist
              [(ngModel)]="selectedRoleId"
              [data]="roles"
              [textField]="'name'"
              [valueField]="'id'"
              [valuePrimitive]="true">
            </kendo-dropdownlist>
            <button kendoButton
                    [themeColor]="'primary'"
                    (click)="saveRole()"
                    [disabled]="isSavingRole || selectedRoleId === selectedDetail.user.roleId">
              {{ isSavingRole ? 'Saving...' : 'Save Role' }}
            </button>
          </div>

          <div class="section">
            <label>Direct Permissions</label>
            <div class="permissions-grid">
              <label class="perm-item" *ngFor="let permission of permissions">
                <input type="checkbox" [(ngModel)]="permissionSelections[permission.id]" />
                <span>{{ permission.name }}</span>
              </label>
            </div>
            <button kendoButton
                    [themeColor]="'primary'"
                    (click)="savePermissions()"
                    [disabled]="isSavingPermissions">
              {{ isSavingPermissions ? 'Saving...' : 'Save Permissions' }}
            </button>
          </div>
        </div>

        <div class="detail empty" *ngIf="!selectedDetail">
          Select a user to edit role and permissions.
        </div>
      </div>
    </div>
  `,
  styles: [`
    .user-role-admin {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .subtitle {
      margin: 4px 0 0;
      color: var(--fv-text-secondary);
      font-size: 13px;
    }

    .actions {
      display: flex;
      gap: 8px;
      align-items: center;
    }

    .toggle {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 12px;
      color: var(--fv-text);
    }

    .layout {
      display: grid;
      grid-template-columns: 260px 1fr;
      gap: 16px;
      min-height: 360px;
    }

    .sidebar {
      display: flex;
      flex-direction: column;
      gap: 10px;
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      padding: 12px;
      background: var(--fv-surface-alt);
    }

    .user-list {
      display: flex;
      flex-direction: column;
      gap: 6px;
      overflow: auto;
      max-height: 420px;
    }

    .user-name {
      font-weight: 600;
      font-size: 13px;
    }

    .user-login {
      font-size: 11px;
      color: var(--fv-text-secondary);
    }

    .detail {
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      padding: 16px;
      background: var(--fv-surface);
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .detail.empty {
      color: var(--fv-text-secondary);
      font-size: 13px;
      align-items: center;
      justify-content: center;
    }

    .detail-header {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .login {
      font-size: 12px;
      color: var(--fv-text-secondary);
    }

    .section {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .section label {
      font-weight: 600;
      font-size: 13px;
    }

    .permissions-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      gap: 8px 12px;
      max-height: 260px;
      overflow: auto;
      padding: 4px 0;
    }

    .perm-item {
      display: flex;
      gap: 6px;
      align-items: center;
      font-size: 12px;
      color: var(--fv-text);
    }

    .error {
      color: var(--fv-status-error-fg);
      font-size: 13px;
    }

    .empty {
      color: var(--fv-text-muted);
      font-size: 12px;
    }

    @media (max-width: 900px) {
      .layout {
        grid-template-columns: 1fr;
      }
    }
  `]
})
export class UserRoleAdminComponent implements OnInit {
  private api = inject(FileVaultApiService);

  users: CommonUser[] = [];
  filteredUsers: CommonUser[] = [];
  roles: CommonRole[] = [];
  permissions: CommonPermission[] = [];
  selectedDetail?: CommonUserDetail;
  selectedRoleId?: number;
  permissionSelections: Record<number, boolean> = {};

  searchTerm = '';
  showInactive = false;
  isSavingRole = false;
  isSavingPermissions = false;
  errorMessage = '';

  ngOnInit(): void {
    this.loadUsers();
    this.loadRoles();
    this.loadPermissions();
  }

  refresh(): void {
    this.loadUsers();
    if (this.selectedDetail) {
      this.selectUser(this.selectedDetail.user);
    }
  }

  async loadUsers(): Promise<void> {
    this.errorMessage = '';
    try {
      const users = await this.api.getCommonUsers(!this.showInactive).toPromise() ?? [];
      this.users = users;
      this.applyFilter();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load users';
    }
  }

  async loadRoles(): Promise<void> {
    try {
      this.roles = await this.api.getCommonRoles().toPromise() ?? [];
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load roles';
    }
  }

  async loadPermissions(): Promise<void> {
    try {
      this.permissions = await this.api.getCommonPermissions().toPromise() ?? [];
      this.permissionSelections = {};
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load permissions';
    }
  }

  applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.filteredUsers = [...this.users];
      return;
    }

    this.filteredUsers = this.users.filter(user =>
      user.displayName.toLowerCase().includes(term) ||
      user.loginId.toLowerCase().includes(term) ||
      user.email.toLowerCase().includes(term)
    );
  }

  async selectUser(user: CommonUser): Promise<void> {
    try {
      this.errorMessage = '';
      const detail = await this.api.getCommonUserDetail(user.id).toPromise();
      if (!detail) return;
      this.selectedDetail = detail;
      this.selectedRoleId = detail.user.roleId;
      this.permissionSelections = this.permissions.reduce((acc, permission) => {
        acc[permission.id] = detail.permissions.some(p => p.id === permission.id);
        return acc;
      }, {} as Record<number, boolean>);
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to load user details';
    }
  }

  async saveRole(): Promise<void> {
    if (!this.selectedDetail || this.selectedRoleId === undefined) return;
    this.isSavingRole = true;
    try {
      await this.api.updateCommonUserRole(this.selectedDetail.user.id, { roleId: this.selectedRoleId }).toPromise();
      this.selectedDetail.user.roleId = this.selectedRoleId;
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to update user role';
    } finally {
      this.isSavingRole = false;
    }
  }

  async savePermissions(): Promise<void> {
    if (!this.selectedDetail) return;
    this.isSavingPermissions = true;
    try {
      const permissionIds = Object.entries(this.permissionSelections)
        .filter(([, selected]) => selected)
        .map(([id]) => Number(id));
      await this.api.updateCommonUserPermissions(this.selectedDetail.user.id, { permissionIds }).toPromise();
    } catch (error: any) {
      this.errorMessage = error?.message || 'Failed to update user permissions';
    } finally {
      this.isSavingPermissions = false;
    }
  }
}
